import React from 'react';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';

export interface ErrorDetails {
  message?: string;
  status?: number;
  statusText?: string;
  data?: any;
}

/**
 * Extracts a user-friendly error message from various error types
 */
export const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Check for apiService error structure (status, statusText, data properties)
    const apiError = error as Error & { status?: number; statusText?: string; data?: any };
    if (apiError.status !== undefined) {
      // Try to extract message from error.data structure
      if (apiError.data?.error?.message) {
        return apiError.data.error.message;
      }
      if (apiError.data?.message) {
        return apiError.data.message;
      }
      if (apiError.statusText) {
        return `${apiError.status} ${apiError.statusText}`;
      }
      // Provide user-friendly messages based on status code
      if (apiError.status === 404) return 'Resource not found';
      if (apiError.status === 401) return 'Unauthorized - Please log in again';
      if (apiError.status === 403) return 'Access forbidden';
      if (apiError.status === 500) return 'Server error - Please try again later';
      if (apiError.status >= 500) return 'Server error - Please try again later';
      if (apiError.status >= 400) return 'Request error - Please check your input';
      return `HTTP error: ${apiError.status}`;
    }
    
    // Check if it's a fetch error with response (axios-style)
    if ('response' in error) {
      const response = (error as any).response;
      if (response?.data?.message) {
        return response.data.message;
      }
      if (response?.statusText) {
        return `${response.status} ${response.statusText}`;
      }
    }
    
    // Check if error message contains HTTP status
    if (error.message.includes('HTTP error')) {
      const match = error.message.match(/status: (\d+)/);
      if (match) {
        const status = parseInt(match[1], 10);
        if (status === 404) return 'Resource not found';
        if (status === 401) return 'Unauthorized - Please log in again';
        if (status === 403) return 'Access forbidden';
        if (status === 500) return 'Server error - Please try again later';
        if (status >= 500) return 'Server error - Please try again later';
        if (status >= 400) return 'Request error - Please check your input';
      }
    }
    
    return error.message || 'An unexpected error occurred';
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred';
};

/**
 * Shows an error notification with enhanced UX
 */
export const showErrorNotification = (
  error: unknown,
  options?: {
    title?: string;
    message?: string;
    autoClose?: number | false;
    id?: string;
  }
) => {
  const errorMessage = options?.message || extractErrorMessage(error);
  const title = options?.title || 'Error';
  
  notifications.show({
    id: options?.id || `error-${Date.now()}`,
    title,
    message: errorMessage,
    color: 'red',
    icon: <IconAlertCircle size={20} />,
    autoClose: options?.autoClose ?? 4000,
    withCloseButton: true,
    variant: 'filled',
    className: 'enhanced-error-notification',
  });
};

/**
 * Shows a success notification with enhanced UX
 */
export const showSuccessNotification = (
  message: string,
  options?: {
    title?: string;
    autoClose?: number | false;
    id?: string;
  }
) => {
  notifications.show({
    id: options?.id || `success-${Date.now()}`,
    title: options?.title || 'Success',
    message,
    color: 'green',
    icon: <IconCircleCheck size={20} />,
    autoClose: options?.autoClose ?? 3000,
    withCloseButton: true,
    variant: 'filled',
    className: 'enhanced-success-notification',
  });
};

/**
 * Shows a warning notification with enhanced UX
 */
export const showWarningNotification = (
  message: string,
  options?: {
    title?: string;
    autoClose?: number | false;
    id?: string;
  }
) => {
  notifications.show({
    id: options?.id || `warning-${Date.now()}`,
    title: options?.title || 'Warning',
    message,
    color: 'orange',
    icon: <IconAlertCircle size={20} />,
    autoClose: options?.autoClose ?? 4000,
    withCloseButton: true,
    variant: 'filled',
    className: 'enhanced-warning-notification',
  });
};

/**
 * Shows an info notification with enhanced UX
 */
export const showInfoNotification = (
  message: string,
  options?: {
    title?: string;
    autoClose?: number | false;
    id?: string;
  }
) => {
  notifications.show({
    id: options?.id || `info-${Date.now()}`,
    title: options?.title || 'Info',
    message,
    color: 'blue',
    icon: <IconInfoCircle size={20} />,
    autoClose: options?.autoClose ?? 4000,
    withCloseButton: true,
    variant: 'filled',
    className: 'enhanced-info-notification',
  });
};

